import { Component } from '@theme/component';
import { onDocumentLoaded, changeMetaThemeColor, setHeaderMenuStyle } from '@theme/utilities';

/**
 * @typedef {Object} HeaderComponentRefs
 * @property {HTMLDivElement} headerDrawerContainer - The header drawer container element
 * @property {HTMLElement} headerMenu - The header menu element
 * @property {HTMLElement} headerRowTop - The header top row element
 */

/**
 * @typedef {CustomEvent<{ minimumReached: boolean }>} OverflowMinimumEvent
 */

/**
 * A custom element that manages the site header.
 *
 * @extends {Component<HeaderComponentRefs>}
 */

class HeaderComponent extends Component {
  requiredRefs = ['headerDrawerContainer', 'headerMenu', 'headerRowTop'];

  /**
   * Width of window when header drawer was hidden
   * @type {number | null}
   */
  #menuDrawerHiddenWidth = null;

  /**
   * An intersection observer for monitoring sticky header position
   * @type {IntersectionObserver | null}
   */
  #intersectionObserver = null;

  /**
   * Whether the header has been scrolled offscreen, when sticky behavior is 'scroll-up'
   * @type {boolean}
   */
  #offscreen = false;

  /**
   * The last recorded scrollTop of the document, when sticky behavior is 'scroll-up
   * @type {number}
   */
  #lastScrollTop = 0;

  /**
   * A timeout to allow for hiding animation, when sticky behavior is 'scroll-up'
   * @type {number | null}
   */
  #timeout = null;

  /**
   * RAF ID for scroll handler throttling
   * @type {number | null}
   */
  #scrollRafId = null;

  /**
   * Keeps the global `--header-height` custom property up to date,
   * which other theme components can then consume
   */
  #resizeObserver = new ResizeObserver(([entry]) => {
    if (!entry || !entry.borderBoxSize[0]) return;

    // The initial height is calculated using the .offsetHeight property, which returns an integer.
    // We round to the nearest integer to avoid unnecessaary reflows.
    const roundedHeaderHeight = Math.round(entry.borderBoxSize[0].blockSize);
    document.body.style.setProperty('--header-height', `${roundedHeaderHeight}px`);

    // Check if the menu drawer should be hidden in favor of the header menu
    if (this.#menuDrawerHiddenWidth && window.innerWidth > this.#menuDrawerHiddenWidth) {
      this.#updateMenuVisibility(false);
    }
  });

  /**
   * Observes the header while scrolling the viewport to track when its actively sticky.
   *
   * - `alwaysSticky=true`: skip the IntersectionObserver entirely. The original
   *   IO trick (threshold=1 + `top:-1px` => "1px offscreen, ratio<1, active")
   *   relied on the header being 1px above the viewport, which breaks the
   *   moment anything pins above it (e.g. a sticky announcement bar pushes
   *   the header to `top: announcement_h - 1px`, fully inside the viewport).
   *   Scroll position alone is the semantically equivalent and geometry-
   *   independent signal — `#updateScrollState` writes `data-sticky-state`
   *   from `scrollY` instead.
   * - `alwaysSticky=false` (scroll-up mode): IntersectionObserver still tracks
   *   `#offscreen` for the scroll-up reveal logic, where geometry isn't
   *   problematic (threshold=0 fires whenever the header crosses the viewport
   *   edge, which still happens regardless of any sticky bar above).
   * @param {Boolean} alwaysSticky - Determines if we need to observe when the header is offscreen
   */
  #observeStickyPosition = (alwaysSticky = true) => {
    if (alwaysSticky) return;
    if (this.#intersectionObserver) return;

    this.#intersectionObserver = new IntersectionObserver(([entry]) => {
      if (!entry) return;
      // 'idle' is treated as offscreen too, even though the header is still
      // visually mid-slide-up, so the scroll listener keeps processing events
      // (e.g. user reverses direction during the slide and we need to flip
      // back to 'active' immediately).
      const stickyState = this.dataset.stickyState;
      this.#offscreen = !entry.isIntersecting || stickyState === 'active' || stickyState === 'idle';

      // Pre-stage 'idle' (sticky position + solid colors + transform fully
      // off-screen above the announcement bar) the moment the header first
      // exits the viewport via natural flow. Without this, the next scroll-up
      // would transition 'inactive' → 'active' directly, with the colors
      // animating from the transparent-mode inverse scheme to the solid-look
      // scheme — visible as a flash on the appearing header. By staging 'idle'
      // while the header is already offscreen, the next reveal is just a
      // translate animation between two solid-colored states. We zero the
      // transition for this single state change so the (already invisible)
      // jump from natural-flow position to sticky-translated position doesn't
      // try to animate.
      if (!entry.isIntersecting && (!stickyState || stickyState === 'inactive')) {
        const previousTransition = this.style.transition;
        this.style.transition = 'none';
        this.dataset.stickyState = 'idle';
        // Force a reflow so the transition: none is committed before the
        // computed-style change from the new state lands.
        void this.offsetHeight;
        this.style.transition = previousTransition;
      }
    }, { threshold: 0 });

    this.#intersectionObserver.observe(this);
  };

  /**
   * Handles the overflow minimum event from the header menu
   * @param {OverflowMinimumEvent} event
   */
  #handleOverflowMinimum = (event) => {
    this.#updateMenuVisibility(event.detail.minimumReached);
  };

  /**
   * Updates the visibility of the menu and drawer
   * @param {boolean} hideMenu - Whether to hide the menu and show the drawer
   */
  #updateMenuVisibility(hideMenu) {
    if (hideMenu) {
      this.#menuDrawerHiddenWidth = window.innerWidth;
    } else {
      this.#menuDrawerHiddenWidth = null;
    }
    setHeaderMenuStyle();
  }

  #handleWindowScroll = () => {
    if (this.#scrollRafId !== null) return;

    this.#scrollRafId = requestAnimationFrame(() => {
      this.#scrollRafId = null;
      this.#updateScrollState();
    });
  };

  #updateScrollState = () => {
    // While a dialog (cart drawer, search modal, etc.) is open, dialog.js
    // applies a body-scroll-lock by setting `body.style.position = 'fixed'`
    // and `top: -<scrollY>`. That zeroes out document.scrollingElement.scrollTop
    // and fires a scroll event — which would otherwise flip data-sticky-state
    // to 'inactive' mid-dialog, un-gating the transparent-header solid-look
    // fallback in header.liquid (the header would re-appear transparent/inverse
    // while the user is just opening the cart). Skip the update while the lock
    // is in effect so the previous sticky-state is preserved (mirrors the
    // syncScrollState defense in layout/theme.liquid).
    if (document.body.style.position === 'fixed') return;

    const stickyMode = this.getAttribute('sticky');
    if (!this.#offscreen && stickyMode !== 'always') return;

    const scrollTop = document.scrollingElement?.scrollTop ?? 0;
    const isScrollingUp = scrollTop < this.#lastScrollTop;
    // Use scroll position rather than the header's bounding rect to detect
    // "page is at the top". The bounding-rect check (headerTop >= 0) was a
    // proxy that assumed sticky-active means top: -1px — that breaks when a
    // sticky announcement bar offsets the header to top: announcement_h - 1px
    // (always positive), which made scroll-up mode immediately reset to
    // 'inactive' and oscillate back-and-forth as the user scrolls up.
    const isAtTop = scrollTop <= 0;

    if (this.#timeout) {
      clearTimeout(this.#timeout);
      this.#timeout = null;
    }

    if (stickyMode === 'always') {
      if (isAtTop) {
        this.dataset.scrollDirection = 'none';
      } else if (isScrollingUp) {
        this.dataset.scrollDirection = 'up';
      } else {
        this.dataset.scrollDirection = 'down';
      }

      // Sticky-state for `sticky=always` lives here (not in the IO callback)
      // so the geometry-fragile threshold-1 trick can be retired. `scrollTop > 0`
      // matches the original semantics of `top: -1px` + threshold=1 and stays
      // correct when a sticky announcement bar is stacked above.
      const newState = scrollTop > 0 ? 'active' : 'inactive';
      if (this.dataset.stickyState !== newState) {
        this.dataset.stickyState = newState;
        if (newState === 'active' && this.dataset.themeColor) {
          changeMetaThemeColor(this.dataset.themeColor);
        }
      }

      this.#lastScrollTop = scrollTop;
      return;
    }

    if (isScrollingUp) {
      if (isAtTop) {
        // reset sticky state when header is scrolled up to natural position
        this.#offscreen = false;
        this.dataset.stickyState = 'inactive';
        this.dataset.scrollDirection = 'none';
      } else {
        // show sticky header when scrolling up
        this.dataset.stickyState = 'active';
        this.dataset.scrollDirection = 'up';
      }
    } else {
      // Scrolling down. Only transition 'active' → 'idle' (this is what
      // triggers the slide-up exit animation in CSS). 'inactive' must NOT
      // be flipped to 'idle' on a first scroll-down from page top — that
      // would re-engage sticky position out of nowhere and make the header
      // animate up off-screen even though it had only just left via natural
      // flow. 'idle' stays 'idle' until the user scrolls back up.
      if (this.dataset.stickyState === 'active') {
        this.dataset.stickyState = 'idle';
      }
      this.dataset.scrollDirection = 'none';
    }

    this.#lastScrollTop = scrollTop;
  };

  connectedCallback() {
    super.connectedCallback();
    this.#resizeObserver.observe(this);
    this.addEventListener('overflowMinimum', this.#handleOverflowMinimum);

    const stickyMode = this.getAttribute('sticky');
    if (stickyMode) {
      this.#observeStickyPosition(stickyMode === 'always');

      if (stickyMode === 'scroll-up' || stickyMode === 'always') {
        document.addEventListener('scroll', this.#handleWindowScroll);
      }

      // Seed initial sticky-state for `sticky=always` so a page reload at
      // scrollY > 0 paints in the correct (active) state without waiting for
      // the first scroll event.
      if (stickyMode === 'always') {
        this.#updateScrollState();
      }
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#resizeObserver.disconnect();
    this.#intersectionObserver?.disconnect();
    this.removeEventListener('overflowMinimum', this.#handleOverflowMinimum);
    document.removeEventListener('scroll', this.#handleWindowScroll);
    if (this.#scrollRafId !== null) {
      cancelAnimationFrame(this.#scrollRafId);
      this.#scrollRafId = null;
    }
    document.body.style.setProperty('--header-height', '0px');
  }
}

if (!customElements.get('header-component')) {
  customElements.define('header-component', HeaderComponent);
}

onDocumentLoaded(() => {
  const header = document.querySelector('header-component');
  const headerGroup = document.querySelector('#header-group');

  // Note: Initial header heights are set via inline script in theme.liquid
  // This ResizeObserver handles dynamic updates after page load

  // Update header group height on resize of any child
  if (headerGroup) {
    const isTransparentHeaderActive = () =>
      !!document.querySelector('.content-for-layout > .shopify-section:first-child [data-transparent-header]');

    const resizeObserver = new ResizeObserver((entries) => {
      const headerGroupHeight = entries.reduce((totalHeight, entry) => {
        if (entry.target !== header || (isTransparentHeaderActive() && header.parentElement?.nextElementSibling)) {
          return totalHeight + (entry.borderBoxSize[0]?.blockSize ?? 0);
        }
        return totalHeight;
      }, 0);
      // The initial height is calculated using the .offsetHeight property, which returns an integer.
      // We round to the nearest integer to avoid unnecessaary reflows.
      const roundedHeaderGroupHeight = Math.round(headerGroupHeight);
      document.body.style.setProperty('--header-group-height', `${roundedHeaderGroupHeight}px`);

      // Keep --announcement-bar-height in sync on resize so the header's
      // top-offset (top: var(--announcement-bar-height) - 1px) stays correct
      // when the announcement bar's text wraps differently at narrower widths.
      const stickyAnnouncementSection = document
        .querySelector('#header-group .announcement-bar[data-sticky="true"]')
        ?.closest('.shopify-section');
      const announcementBarHeight =
        stickyAnnouncementSection instanceof HTMLElement ? Math.round(stickyAnnouncementSection.offsetHeight) : 0;
      document.body.style.setProperty('--announcement-bar-height', `${announcementBarHeight}px`);
    });

    if (header instanceof HTMLElement) {
      resizeObserver.observe(header);
    }

    // Observe all children of the header group
    const children = headerGroup.children;
    for (let i = 0; i < children.length; i++) {
      const element = children[i];
      if (element instanceof HTMLElement) {
        resizeObserver.observe(element);
      }
    }

    // Also observe the header group itself for child changes
    const mutationObserver = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (mutation.type === 'childList') {
          // Re-observe all children when the list changes
          const children = headerGroup.children;
          for (let i = 0; i < children.length; i++) {
            const element = children[i];
            if (element instanceof HTMLElement) {
              resizeObserver.observe(element);
            }
          }
        }
      }
    });

    mutationObserver.observe(headerGroup, { childList: true });
  }
});
